import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useMasterMaps, { selCls, inputCls } from "../hooks/useMasterMaps";
import { EMPLOYMENT_STATUSES, HIRE_SOURCES, EVENT_TYPES } from "../lib/constants";
import { toast } from "sonner";
import { Plus, Pencil, History, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

function EmpStatusBadge({ value }) {
  const map = {
    Active: "text-[#1B7F3B] border-[#1B7F3B]/30 bg-[#1B7F3B]/5",
    "On Notice": "text-[#D97706] border-[#D97706]/30 bg-[#D97706]/5",
    Exited: "text-[#6B7280] border-gray-300 bg-gray-50",
    Absconded: "text-[#BB162B] border-[#BB162B]/30 bg-[#BB162B]/5",
  };
  return <span className={`inline-block border px-2 py-0.5 text-[11px] font-medium rounded-sm whitespace-nowrap ${map[value]}`}>{value}</span>;
}

const EMPTY = {
  employee_code: "", full_name: "", branch_id: "", dept_id: "", desig_id: "",
  date_of_joining: "", employment_status: "Active", date_of_resignation: "", last_working_day: "",
  source_of_hire: "", ctc: "", reporting_to_id: "",
};

export default function Employees() {
  const { can } = useAuth();
  const canWrite = can("employees", "write");
  const maps = useMasterMaps();
  const [data, setData] = useState({ total: 0, rows: [] });
  const [f, setF] = useState({ branch_id: "", dept_id: "", status: "", search: "" });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [eventsFor, setEventsFor] = useState(null);
  const [events, setEvents] = useState([]);
  const [evForm, setEvForm] = useState({ event_type: "", event_date: "", to_branch_id: "", to_desig_id: "", last_working_day: "", remarks: "" });
  const limit = 50;

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit, skip: page * limit };
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/employees", { params }).then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [f, page]);
  useEffect(load, [load]);

  const openCreate = () => { setForm(EMPTY); setEditing(null); setDialogOpen(true); };
  const openEdit = (r) => {
    setForm(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, r[k] ?? ""])));
    setEditing(r);
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const { data: res } = await api.put(`/employees/${editing.emp_id}`, form);
        toast.success("Employee updated");
        if (res.auto_drafted_requisition)
          toast.info(`Replacement requisition ${res.auto_drafted_requisition.req_no} auto-drafted`, { duration: 6000 });
      } else {
        await api.post("/employees", form);
        toast.success("Employee added");
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const openEvents = (r) => {
    setEventsFor(r);
    setEvForm({ event_type: "", event_date: "", to_branch_id: "", to_desig_id: "", last_working_day: "", remarks: "" });
    api.get(`/employees/${r.emp_id}/events`).then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  };

  const addEvent = async () => {
    try {
      const { data: res } = await api.post(`/employees/${eventsFor.emp_id}/events`, evForm);
      toast.success(`${evForm.event_type} recorded`);
      if (res.auto_drafted_requisition)
        toast.info(`Replacement requisition ${res.auto_drafted_requisition.req_no} auto-drafted`, { duration: 6000 });
      setEventsFor(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const label = (t, req) => (
    <label className="block text-xs font-medium text-[#6B7280] uppercase tracking-wider mb-1.5">
      {t}{req && <span className="text-[#BB162B]"> *</span>}
    </label>
  );
  const desigOptions = form.dept_id ? maps.designations.filter((d) => d.dept_id === form.dept_id) : maps.designations;

  return (
    <div data-testid="employees-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">Employee Register</h1>
          <p className="text-sm text-[#6B7280] mt-1">Active headcount is derived from this register — never typed into a dashboard</p>
        </div>
        {canWrite && (
          <button data-testid="employee-add-button" onClick={openCreate}
            className="flex items-center gap-2 bg-[#0E2240] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#1a3358] transition-colors duration-200 self-start">
            <Plus size={15} /> Add Employee
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <input data-testid="employee-search-input" className={`${selCls} pl-8 w-52`} placeholder="Name or code…"
            value={f.search} onChange={(e) => { setF((p) => ({ ...p, search: e.target.value })); setPage(0); }} />
        </div>
        <select data-testid="employee-branch-filter" className={selCls} value={f.branch_id}
          onChange={(e) => { setF((p) => ({ ...p, branch_id: e.target.value })); setPage(0); }}>
          <option value="">All branches</option>
          {maps.branches.map((b) => <option key={b.branch_id} value={b.branch_id}>{b.branch_name}</option>)}
        </select>
        <select data-testid="employee-dept-filter" className={selCls} value={f.dept_id}
          onChange={(e) => { setF((p) => ({ ...p, dept_id: e.target.value })); setPage(0); }}>
          <option value="">All departments</option>
          {maps.departments.map((d) => <option key={d.dept_id} value={d.dept_id}>{d.dept_name}</option>)}
        </select>
        <select data-testid="employee-status-filter" className={selCls} value={f.status}
          onChange={(e) => { setF((p) => ({ ...p, status: e.target.value })); setPage(0); }}>
          <option value="">All statuses</option>
          {EMPLOYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="employees-table">
            <thead>
              <tr className="border-b border-gray-200 bg-[#F5F6F8]">
                {["Code", "Name", "Branch", "Department", "Designation", "DOJ", "Source", "CTC", "Status", ""].map((h, i) => (
                  <th key={i} className={`px-3 py-3 text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold whitespace-nowrap ${h === "CTC" ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-[#6B7280]">Loading…</td></tr>
              ) : data.rows.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-[#6B7280]">No employees found</td></tr>
              ) : data.rows.map((r) => (
                <tr key={r.emp_id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs">{r.employee_code}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap font-medium">{r.full_name}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{maps.branchMap[r.branch_id] || "—"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{maps.deptMap[r.dept_id] || "—"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{maps.desigMap[r.desig_id] || "—"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap tabular-nums text-xs">{r.date_of_joining}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs text-[#6B7280]">{r.source_of_hire || "—"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap tabular-nums text-right text-xs">{r.ctc ? `₹${Number(r.ctc).toLocaleString("en-IN")}` : "—"}</td>
                  <td className="px-3 py-2.5"><EmpStatusBadge value={r.employment_status} /></td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-right">
                    <button data-testid={`employee-events-${r.emp_id}`} onClick={() => openEvents(r)}
                      className="text-[#6B7280] hover:text-[#0E2240] p-1 transition-colors duration-200" aria-label="Events">
                      <History size={15} />
                    </button>
                    {canWrite && (
                      <button data-testid={`employee-edit-${r.emp_id}`} onClick={() => openEdit(r)}
                        className="text-[#1E5AA8] hover:text-[#0E2240] p-1 ml-1 transition-colors duration-200" aria-label="Edit">
                        <Pencil size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-gray-200 flex items-center justify-between text-xs text-[#6B7280]">
          <span className="tabular-nums">{data.total} employees · page {page + 1} of {Math.max(1, Math.ceil(data.total / limit))}</span>
          <div className="flex gap-2">
            <button data-testid="employee-prev-page" disabled={page === 0} onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 border border-gray-300 rounded-sm disabled:opacity-40 hover:bg-gray-50 transition-colors duration-200">Prev</button>
            <button data-testid="employee-next-page" disabled={(page + 1) * limit >= data.total} onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 border border-gray-300 rounded-sm disabled:opacity-40 hover:bg-gray-50 transition-colors duration-200">Next</button>
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white rounded-sm shadow-none border border-gray-200 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#0E2240]">{editing ? `Edit ${editing.full_name}` : "New Employee"}</DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              Setting status to "On Notice" auto-drafts a replacement requisition.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div>{label("Employee code", true)}<input data-testid="emp-field-code" className={inputCls} value={form.employee_code} onChange={(e) => setForm((p) => ({ ...p, employee_code: e.target.value }))} /></div>
            <div>{label("Full name", true)}<input data-testid="emp-field-name" className={inputCls} value={form.full_name} onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))} /></div>
            <div>{label("Branch", true)}
              <select data-testid="emp-field-branch" className={inputCls} value={form.branch_id} onChange={(e) => setForm((p) => ({ ...p, branch_id: e.target.value }))}>
                <option value="">— Select —</option>
                {maps.branches.map((b) => <option key={b.branch_id} value={b.branch_id}>{b.branch_name}</option>)}
              </select></div>
            <div>{label("Department", true)}
              <select data-testid="emp-field-dept" className={inputCls} value={form.dept_id} onChange={(e) => setForm((p) => ({ ...p, dept_id: e.target.value, desig_id: "" }))}>
                <option value="">— Select —</option>
                {maps.departments.map((d) => <option key={d.dept_id} value={d.dept_id}>{d.dept_name}</option>)}
              </select></div>
            <div>{label("Designation", true)}
              <select data-testid="emp-field-desig" className={inputCls} value={form.desig_id} onChange={(e) => setForm((p) => ({ ...p, desig_id: e.target.value }))}>
                <option value="">— Select —</option>
                {desigOptions.map((d) => <option key={d.desig_id} value={d.desig_id}>{d.desig_name}</option>)}
              </select></div>
            <div>{label("Date of joining", true)}<input data-testid="emp-field-doj" type="date" className={inputCls} value={form.date_of_joining} onChange={(e) => setForm((p) => ({ ...p, date_of_joining: e.target.value }))} /></div>
            <div>{label("Source of hire")}
              <select data-testid="emp-field-source" className={inputCls} value={form.source_of_hire} onChange={(e) => setForm((p) => ({ ...p, source_of_hire: e.target.value }))}>
                <option value="">— Select —</option>
                {HIRE_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select></div>
            <div>{label("CTC (₹/yr)")}<input data-testid="emp-field-ctc" type="number" className={inputCls} value={form.ctc} onChange={(e) => setForm((p) => ({ ...p, ctc: e.target.value }))} /></div>
            {editing && (
              <div>{label("Status")}
                <select data-testid="emp-field-status" className={inputCls} value={form.employment_status} onChange={(e) => setForm((p) => ({ ...p, employment_status: e.target.value }))}>
                  {EMPLOYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select></div>
            )}
            {editing && form.employment_status === "On Notice" && (
              <>
                <div>{label("Resignation date")}<input data-testid="emp-field-resig" type="date" className={inputCls} value={form.date_of_resignation || ""} onChange={(e) => setForm((p) => ({ ...p, date_of_resignation: e.target.value }))} /></div>
                <div>{label("Last working day")}<input data-testid="emp-field-lwd" type="date" className={inputCls} value={form.last_working_day || ""} onChange={(e) => setForm((p) => ({ ...p, last_working_day: e.target.value }))} /></div>
              </>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors duration-200">Cancel</button>
            <button data-testid="employee-save-button" onClick={save} disabled={saving}
              className="px-4 py-2 text-sm bg-[#0E2240] text-white rounded-sm hover:bg-[#1a3358] transition-colors duration-200 disabled:opacity-50">
              {saving ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!eventsFor} onOpenChange={(o) => !o && setEventsFor(null)}>
        <DialogContent className="bg-white rounded-sm shadow-none border border-gray-200 max-w-lg max-h-[85vh] overflow-y-auto">
          {eventsFor && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-[#0E2240]">{eventsFor.full_name} — Events</DialogTitle>
                <DialogDescription className="text-xs text-[#6B7280]">
                  {eventsFor.employee_code} · {maps.branchMap[eventsFor.branch_id]} · {maps.desigMap[eventsFor.desig_id]}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {events.map((ev) => (
                  <div key={ev.event_id} className="border border-gray-200 rounded-sm p-2.5 text-xs">
                    <span className="font-semibold text-[#0E2240]">{ev.event_type}</span>
                    <span className="text-[#6B7280]"> · {ev.event_date}</span>
                    {ev.remarks && <div className="text-[#6B7280] mt-0.5">{ev.remarks}</div>}
                  </div>
                ))}
                {events.length === 0 && <div className="text-xs text-[#6B7280]">No events recorded</div>}
              </div>
              {can("employees", "write") && !["Exited", "Absconded"].includes(eventsFor.employment_status) && (
                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Record event</div>
                  <div className="grid grid-cols-2 gap-3">
                    <select data-testid="event-type-select" className={inputCls} value={evForm.event_type}
                      onChange={(e) => setEvForm((p) => ({ ...p, event_type: e.target.value }))}>
                      <option value="">— Event type —</option>
                      {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input data-testid="event-date-input" type="date" className={inputCls} value={evForm.event_date}
                      onChange={(e) => setEvForm((p) => ({ ...p, event_date: e.target.value }))} />
                    {evForm.event_type === "Resignation" && (
                      <div className="col-span-2">
                        <label className="block text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Last working day</label>
                        <input data-testid="event-lwd-input" type="date" className={inputCls} value={evForm.last_working_day}
                          onChange={(e) => setEvForm((p) => ({ ...p, last_working_day: e.target.value }))} />
                      </div>
                    )}
                    {evForm.event_type === "Transfer In" && (
                      <select data-testid="event-branch-select" className={`${inputCls} col-span-2`} value={evForm.to_branch_id}
                        onChange={(e) => setEvForm((p) => ({ ...p, to_branch_id: e.target.value }))}>
                        <option value="">— Transfer to branch —</option>
                        {maps.branches.map((b) => <option key={b.branch_id} value={b.branch_id}>{b.branch_name}</option>)}
                      </select>
                    )}
                    {evForm.event_type === "Promotion" && (
                      <select data-testid="event-desig-select" className={`${inputCls} col-span-2`} value={evForm.to_desig_id}
                        onChange={(e) => setEvForm((p) => ({ ...p, to_desig_id: e.target.value }))}>
                        <option value="">— Promote to designation —</option>
                        {maps.designations.map((d) => <option key={d.desig_id} value={d.desig_id}>{d.desig_name}</option>)}
                      </select>
                    )}
                    <input data-testid="event-remarks-input" className={`${inputCls} col-span-2`} placeholder="Remarks"
                      value={evForm.remarks} onChange={(e) => setEvForm((p) => ({ ...p, remarks: e.target.value }))} />
                  </div>
                  <button data-testid="event-save-button" onClick={addEvent} disabled={!evForm.event_type}
                    className="w-full bg-[#0E2240] text-white text-sm py-2 rounded-sm hover:bg-[#1a3358] transition-colors duration-200 disabled:opacity-50">
                    Record Event
                  </button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
